import React, { useState, useEffect } from 'react';
import { Card, Alert, Spin, Typography, Space, Tag } from 'antd';
import { EyeOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import NewPatientPortalHealthOverview from './NewPatientPortalHealthOverview';

const { Title, Text } = Typography;

const StaffPreviewHealthOverview = ({ patientId }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [staffPreviewInfo, setStaffPreviewInfo] = useState(null);

  useEffect(() => {
    const fetchPreviewData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch staff preview data using clinical authentication
        const response = await axios.get(`/portal/admin/preview/${patientId}/health-overview`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = response.data;
        
        // Extract staff preview metadata
        if (data._staffPreview) {
          setStaffPreviewInfo(data._staffPreview);
          // Remove metadata from the data that will be passed to the portal component
          const { _staffPreview, ...portalData } = data;
          setPreviewData(portalData);
        } else {
          setPreviewData(data);
        }

      } catch (err) {
        console.error('Error fetching staff preview health overview:', err);
        setError(err.response?.data?.message || 'Failed to load health overview preview');
      } finally {
        setLoading(false);
      }
    };

    if (patientId && token) {
      fetchPreviewData();
    }
  }, [patientId, token]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Preview Error"
        description={error}
        type="error"
        showIcon
        style={{ margin: '20px' }}
      />
    );
  }

  return (
    <div>
      {/* Staff Preview Info Card */}
      {staffPreviewInfo && (
        <Card 
          size="small" 
          style={{ 
            marginBottom: '20px',
            background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)',
            border: '1px solid #91d5ff'
          }}
        >
          <Space>
            <EyeOutlined style={{ color: '#1890ff' }} />
            <Text strong style={{ color: '#1890ff' }}>Staff Preview Mode</Text>
            <Text type="secondary">|</Text>
            <Text type="secondary">
              <ClockCircleOutlined style={{ marginRight: '4px' }} />
              {new Date(staffPreviewInfo.previewedAt).toLocaleString()}
            </Text>
            <Text type="secondary">|</Text>
            <Text type="secondary">Previewed by: {staffPreviewInfo.previewedBy}</Text>
          </Space>
        </Card>
      )}

      {/* Patient Portal Health Overview Component */}
      <div style={{ 
        border: '2px dashed #d9d9d9',
        borderRadius: '8px',
        padding: '1px',
        background: '#fafafa'
      }}>
        <div style={{ 
          background: '#fff',
          borderRadius: '6px',
          minHeight: '400px'
        }}>
          {/* Create a mock portal context for the component */}
          <MockPortalProvider previewData={previewData}>
            <NewPatientPortalHealthOverview />
          </MockPortalProvider>
        </div>
      </div>
    </div>
  );
};

// Mock Portal Provider to simulate patient portal context
const MockPortalProvider = ({ children, previewData }) => {
  // Create a mock context that mimics the PatientPortalContext
  const mockPortalContext = {
    patient: previewData?.patient,
    isAuthenticated: true,
    loading: false,
    fetchHealthOverview: () => Promise.resolve(previewData),
    // Add other methods as needed
  };

  // We'll need to modify NewPatientPortalHealthOverview to accept props
  // or create a wrapper that provides the data directly
  return (
    <div data-preview-mode="true">
      {React.cloneElement(children, { 
        previewData: previewData,
        isStaffPreview: true 
      })}
    </div>
  );
};

export default StaffPreviewHealthOverview;
